import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { setTourExpandedKeys } from '@/lib/tour/expanded';
import { setTourOpenPanels } from '@/lib/tour/panels';
import { setTourOpenTab } from '@/lib/tour/tabs';
import { TOUR_STEPS, type TourPlacement, type TourStep } from '@/lib/tour/steps';

/**
 * 안내 투어 화면 — 배경을 어둡게 깔고 설명할 기능 하나만 밝힌다.
 *
 * ## 구멍은 그림자로 판다
 *
 * 어두운 막에 실제로 구멍을 뚫으려면 SVG 마스크나 네 조각으로 나눈 사각형이 필요하다. 여기서는
 * 대상 크기의 빈 상자에 <b>아주 넓은 box-shadow</b>({@code 0 0 0 9999px})를 주는 방법을 쓴다.
 * 상자 밖이 전부 어두워지므로 상자 자리만 원래 화면이 비친다 — 요소 하나로 끝나고, 위치가 바뀔 때
 * 네 조각을 따로 맞출 일이 없다.
 *
 * ## 안내 중에는 아무것도 눌리지 않는다
 *
 * 화면 전체를 덮는 투명한 막이 클릭을 받아 삼킨다. 밝혀 놓은 버튼도 눌리지 않는다 —
 * 설명 도중에 다른 화면으로 떠나 버리면 안내가 허공에서 끊긴다. 이동은 안내를 닫은 뒤에 한다.
 *
 * ## 없는 것은 설명하지 않는다
 *
 * 대상은 `data-tour` 이름으로 찾고, <b>시작할 때 화면에 실제로 보이는 것만</b> 대본에 남긴다.
 * 권한이 없어 안 보이는 버튼, 모바일에서 서랍에 접힌 사이드바가 여기서 걸러진다. 그래서 단계 수는
 * 사람마다 다르고, "3 / 7" 의 분모도 그 사람이 실제로 볼 걸음 수다.
 *
 * ## 다 그려지기를 기다렸다가 센다
 *
 * 이 셈을 한 번만 하면 <b>언제 열었느냐에 따라 안내 내용이 달라진다.</b> 실제로 그랬다 — 첫 로그인에
 * 저절로 뜬 안내는 헤더 버튼(?)으로 연 것보다 걸음이 적었다. 자동 실행은 화면이 아직 그려지는 중에
 * 걸리고, 손으로 여는 것은 한참 뒤라 그때는 모든 것이 자리에 있기 때문이다.
 *
 * 그래서 <b>같은 결과가 연달아 몇 번 나올 때까지</b> 다시 센다. 두 번으로는 모자랐다 — 마침 그 사이에
 * 아무 응답도 오지 않으면 굳어 버린다. 이미 다 그려져 있으면 {@link SETTLE_STABLE_SAMPLES} 번을 세는
 * 만큼만 늦고(0.2초 남짓), 아직 오는 중이면 도착할 때까지 기다린다. 영영 안 오는 것(서버가 죽어 위젯이
 * 끝내 비는 경우)까지 붙잡지 않도록 {@link SETTLE_TIMEOUT} 에서 끊는다.
 */

type Rect = { top: number; left: number; width: number; height: number };

/** 하이라이트가 대상에 딱 붙지 않도록 두는 여백. */
const HOLE_PADDING = 8;
/** 하이라이트와 설명 카드 사이. */
const GAP = 14;
/** 화면 가장자리에서 띄우는 최소 거리. */
const EDGE = 12;
const CARD_MAX_WIDTH = 340;
const DIM = 'rgba(15, 23, 42, 0.62)';

/** 대본이 굳었는지 다시 세어 보는 간격(ms). */
const SETTLE_INTERVAL = 100;
/** 결과가 이만큼 연달아 같으면 화면이 다 그려진 것으로 본다. 곱하면 안내가 뜨기까지의 최소 지연이다. */
const SETTLE_STABLE_SAMPLES = 3;
/** 여기까지는 기다린다(ms). 끝내 오지 않는 것 때문에 안내가 안 뜨는 일은 없어야 한다. */
const SETTLE_TIMEOUT = 3_000;

export function TourOverlay({
  steps = TOUR_STEPS,
  finishLabel = '시작하기',
  onClose,
}: {
  steps?: TourStep[];
  /**
   * 마지막 걸음의 버튼 글자.
   *
   * 첫 로그인 안내는 여기서 서비스를 "시작" 하지만, 화면별 안내(?)는 이미 하던 일로 돌아갈 뿐이다 —
   * 보던 화면에서 "시작하기" 를 누르면 무엇이 시작되는지 알 수 없다.
   */
  finishLabel?: string;
  /** `completed` 는 끝까지 봤는지 여부. 건너뛰기·Esc 로 닫으면 false 다. */
  onClose: (completed: boolean) => void;
}) {
  // 대본 확정은 마운트 뒤로 미룬다 — 렌더 중에는 형제 요소(헤더·사이드바)가 아직 DOM 에 없을 수 있다.
  const [runnable, setRunnable] = useState<TourStep[] | null>(null);
  const [index, setIndex] = useState(0);

  // 같은 결과가 연달아 나올 때까지 다시 센다(위 "다 그려지기를 기다렸다가 센다").
  useEffect(() => {
    let previousShape: string | null = null;
    let stable = 0;
    let waited = 0;
    let timer = 0;

    const sample = () => {
      const visible = steps.filter((step) => {
        /*
          탭을 여는 걸음은 <b>탭 버튼</b>으로 판정한다. 대상(탭 안의 요소)은 지금 없는 것이 정상이지만,
          그렇다고 무조건 살리면 <b>권한이 없어 탭조차 없는 사람</b>에게 그 탭 설명이 뜬다 —
          열리지도 않을 화면을 밝히지 못한 채 카드만 뜨는 걸음이다.
        */
        /*
          ★ <b>{@code requires} 를 단 걸음은 예외</b>다(2026-09-28). 버튼이 아예 없는 탭이 생겼다 —
            현장 조회의 「개요」는 고르는 것이 아니라 들어오면 처음 보는 화면이라 목차에 줄이
            없다({@code SiteTabTree}). 그런 탭은 <b>버튼으로 권한을 물을 수 없으므로</b> 대본이
            권한을 직접 적고({@code AppLayout} 이 세션으로 판정한다), 여기서는 묻지 않는다.
        */
        if (step.openTabKey && !step.requires && !isVisible(findTarget(step.openTabKey))) return false;

        return (
          step.target === null
          // 판을 열어 주거나 사이드바를 대신 펼치는 걸음의 대상은 <b>지금은 없는 것이 정상</b>이다 —
          // 그 걸음에 닿으면 열리고 펼쳐진다. 탭도 같다.
          || step.openTabKey != null
          || (step.openPanelKeys?.length ?? 0) > 0
          || (step.expandMenuKeys?.length ?? 0) > 0
          || isVisible(findTarget(step.target))
        );
      });
      const shape = visible.map((step) => step.key).join('|');

      if (shape === previousShape) {
        stable += 1;
      } else {
        // 결과가 달라졌다 — 뭔가 방금 그려졌다는 뜻이니 세던 것을 처음부터 다시 센다.
        previousShape = shape;
        stable = 1;
      }

      if (stable >= SETTLE_STABLE_SAMPLES || waited >= SETTLE_TIMEOUT) {
        setRunnable(visible);
        return;
      }

      waited += SETTLE_INTERVAL;
      timer = window.setTimeout(sample, SETTLE_INTERVAL);
    };

    timer = window.setTimeout(sample, 0);
    return () => window.clearTimeout(timer);
  }, [steps]);

  const step = runnable?.[index] ?? null;
  const total = runnable?.length ?? 0;
  const isLast = total > 0 && index === total - 1;

  const finish = useCallback((completed: boolean) => onClose(completed), [onClose]);
  /*
    넘기기는 <b>걸음 번호를 확인하고</b> 한다. 직접 해 보는 걸음에서는 같은 일이 둘로 들어온다 — 사람이 누른 클릭과
    「누르면 나타나는 것」(advanceWhen) 감지. 둘 다 넘기면 한 걸음을 건너뛴다.
  */
  const indexRef = useRef(index);
  indexRef.current = index;
  /** 가던 방향. 대상이 없어 건너뛸 때 [이전] 으로 왔으면 뒤로, 아니면 앞으로 넘긴다. */
  const direction = useRef<1 | -1>(1);
  const advanceFrom = useCallback(
    (from: number) => {
      if (indexRef.current !== from) return;
      direction.current = 1;
      if (from >= total - 1) finish(true);
      else setIndex(from + 1);
    },
    [finish, total],
  );
  const prev = useCallback(() => {
    direction.current = -1;
    setIndex((current) => Math.max(0, current - 1));
  }, []);

  /*
    <b>짚을 것이 없는 걸음은 건너뛴다</b>(사용자 지적 2026-11-02 — 「하이라이트 없는 창은 뭔가 문제가 있는 거잖아」).
    직접 해 보는 걸음은 앞 걸음의 결과로 대상이 생기므로 시작할 때 거르지 않는다(위 걸러내기의 예외). 대신 걸음에
    닿으면 2초까지 기다려 보고, 그래도 없으면 가던 방향으로 넘긴다 — 아직 시작 안 한 현장의 [편집], 줄이 없는 표의
    [삭제] 같은 것. 기다리는 동안에는 카드를 띄우지 않는다(아래 found). 대상이 <b>있는데 창에 가린</b> 경우는
    건너뛰지 않는다 — 카드가 「창을 닫으라」 고 말한다.
  */
  const [found, setFound] = useState(false);
  /*
    ★ 걸음이 <b>실제로 바뀔 때만</b> 처음부터 다시 본다(이름 + 차례). 걸음 목록·손잡이는 화면이 다시 그려질 때마다
      새 객체가 되는데(세션 갱신 등), 그것을 의존성으로 두면 「기다림」 이 계속 처음으로 돌아가 카드가 사라진
      채 남았다(2026-11-02 · 공정관리 [최종 저장] 걸음). 나머지는 참조로 읽는다.
  */
  const stepKey = step ? `${index}:${step.key}` : '';
  const liveRef = useRef({ step, runnable, advanceFrom });
  liveRef.current = { step, runnable, advanceFrom };
  useEffect(() => {
    const { step: current, runnable: list } = liveRef.current;
    setFound(false);
    const target = current?.target;
    if (!target || !current?.openTabKey) {
      setFound(true);
      return;
    }
    const from = indexRef.current;
    const started = performance.now();
    /*
      얼마나 기다리나 — 바로 앞 걸음(온 방향)이 버튼을 누르는 걸음이었으면 그 결과(열린 칸·새 줄)가 그려질 틈으로
      2초, 아니면 0.4초. 없는 걸음이 연달아 있을 때 걸음마다 2초씩 빈 화면이 이어지지 않게(사용자 지적 2026-11-02).
    */
    const before = list?.[from - direction.current];
    const patience = before?.act === 'click' || before?.advanceWhen ? 2000 : 400;
    const timer = window.setInterval(() => {
      const element = findTarget(target);
      if (element && isVisible(element)) {
        window.clearInterval(timer);
        setFound(true);
        return;
      }
      if (performance.now() - started < patience) return;
      window.clearInterval(timer);
      if (indexRef.current !== from) return;
      if (direction.current === -1 && from > 0) setIndex(from - 1);
      else liveRef.current.advanceFrom(from);
    }, 100);
    return () => window.clearInterval(timer);
  }, [stepKey]);

  /*
    [다음] — 「누르기」 걸음이면 안내가 그 버튼을 <b>대신 누른다</b>(사용자 지시 2026-11-02). 누른 클릭은 아래
    클릭 감지가 받아 넘긴다. 버튼이 꺼져 있으면 대신 못 누른다고 카드에 적고, 한 번 더 누르면 그냥 넘어간다.
  */
  const [blocked, setBlocked] = useState<string | null>(null);
  useEffect(() => setBlocked(null), [index]);
  // 칸을 채워 버튼이 켜지면 「못 누른다」 를 거두고 [다음] 을 다시 「대신 누르기」 로 돌린다.
  useEffect(() => {
    if (!blocked || step?.act !== 'click' || !step.target) return;
    const name = step.target;
    const timer = window.setInterval(() => {
      const element = findTarget(name) as HTMLButtonElement | null;
      if (element && !element.disabled && isVisible(element)) setBlocked(null);
    }, 300);
    return () => window.clearInterval(timer);
  }, [blocked, step]);
  const next = useCallback(() => {
    if (step?.act === 'click' && step.target && !blocked) {
      const element = findTarget(step.target);
      const off = !element || !isVisible(element) || (element as HTMLButtonElement).disabled;
      if (off) {
        setBlocked(element ? '아직 누를 수 없습니다 — 앞의 칸을 먼저 채우면 켜집니다.' : '지금 화면에는 이 버튼이 없습니다.');
        return;
      }
      element.click();
      return;
    }
    advanceFrom(index);
  }, [step, blocked, advanceFrom, index]);

  /* 사람이 짚은 버튼을 직접 누르거나(누르기) 칸의 값을 바꾸면(고르기) 다음으로 간다. 화면이 그려질 틈을 준다. */
  useEffect(() => {
    const act = step?.act;
    const target = step?.target;
    if (!act || !target) return;
    const from = index;
    const kind = act === 'click' ? 'click' : 'change';
    const onEvent = (event: Event) => {
      const element = findTarget(target);
      if (!element || !(event.target instanceof Node) || !element.contains(event.target)) return;
      window.setTimeout(() => advanceFrom(from), act === 'click' ? 450 : 300);
    };
    /*
      <b>꺼진 버튼을 누른 손</b>도 받는다(사용자 지적 2026-11-02). 꺼진 버튼은 클릭이 아예 안 일어나서, 사람은 눌렀는데
      아무 일도 없는 화면만 본다. 그 자리를 누른 순간을 재서 「왜 안 눌리는지」 를 카드에 적는다.
    */
    const onPress = (event: PointerEvent) => {
      if (act !== 'click') return;
      const element = findTarget(target) as HTMLButtonElement | null;
      if (!element?.disabled) return;
      const box = element.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) return;
      setBlocked('아직 누를 수 없습니다 — 앞의 칸을 먼저 채우면 켜집니다.');
    };
    document.addEventListener(kind, onEvent, true);
    document.addEventListener('pointerdown', onPress, true);
    return () => {
      document.removeEventListener(kind, onEvent, true);
      document.removeEventListener('pointerdown', onPress, true);
    };
  }, [step, index, advanceFrom]);

  // 보여 줄 걸음이 하나도 없으면(있을 수 없는 조합이지만) 빈 막을 남기지 않는다.
  useEffect(() => {
    if (runnable !== null && runnable.length === 0) finish(false);
  }, [runnable, finish]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // 칸에 적는 중의 화살표·Esc 는 그 칸의 것이다 — 직접 해 보는 걸음에서 커서를 옮기다 안내가 넘어가면 안 된다.
      if (isTypingTarget(event.target)) return;
      if (event.key === 'Escape') finish(false);
      else if (event.key === 'ArrowRight') next();
      else if (event.key === 'ArrowLeft') prev();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [finish, next, prev]);

  /*
    「이것을 누르세요」 걸음은 누른 결과가 화면에 나타나면 저절로 다음으로 간다({@link TourStep.advanceWhen}).
    걸음에 닿기 전부터 이미 있으면(앞에서 열어 둔 편집 화면) 곧장 넘긴다 — 이미 한 일을 또 시키지 않는다.
  */
  useEffect(() => {
    const name = step?.advanceWhen;
    if (!name) return;
    const from = index;
    const timer = window.setInterval(() => {
      if (isVisible(findTarget(name))) {
        window.clearInterval(timer);
        advanceFrom(from);
      }
    }, 250);
    return () => window.clearInterval(timer);
  }, [step, index, advanceFrom]);

  // 접혀 있는 것을 설명하려면 대신 펼쳐 줘야 한다. 걸음을 옮기면 이전 걸음이 펼친 것은 도로 접힌다.
  useEffect(() => {
    setTourExpandedKeys(step?.expandMenuKeys ?? []);
  }, [step]);

  // 눌러야 나오는 판(옆판·모달)도 대신 열어 준다. 같은 사정이다 — 투어 중에는 못 누르고,
  // 열려 있으면 ? 버튼이 가려 그 안에서 안내를 시작할 수도 없다.
  useEffect(() => {
    setTourOpenPanels(step?.openPanelKeys ?? []);
  }, [step]);

  // 탭도 대신 열어 준다 — 탭 <b>안</b>의 버튼이 무엇을 하는지 말하려면 그 탭이 열려 있어야 한다.
  // 주소(`?tab=`)는 건드리지 않으므로 안내가 끝나면 보던 탭으로 돌아온다.
  useEffect(() => {
    setTourOpenTab(step?.openTabKey ?? null);
  }, [step]);

  /*
    그 탭 버튼을 <b>어둠 위로 올린다</b>(사용자 지시 2026-08-27).

    <p>반짝이는 고리만으로는 모자랐다 — 막이 버튼까지 덮고 있어 «어느 탭 이야기인가» 를 읽으려면
    어두운 글자를 들여다봐야 한다. 걸음 동안만 그 버튼을 막보다 위로 올려 <b>이름이 그대로 읽히게</b>
    한다. 인라인 값만 손대고 걸음이 끝나면 되돌리므로, 화면 쪽 코드는 이 사정을 몰라도 된다.
  */
  useEffect(() => {
    const name = step?.openTabKey;
    if (!name) return;

    const element = findTarget(name);
    if (!element) return;

    const previous = { position: element.style.position, zIndex: element.style.zIndex };
    // 이미 자리를 잡아 둔 요소(absolute·fixed)의 배치를 흔들지 않는다 — z-index 만 올리면 된다.
    if (!previous.position) element.style.position = 'relative';
    element.style.zIndex = '1003';

    return () => {
      element.style.position = previous.position;
      element.style.zIndex = previous.zIndex;
    };
  }, [step]);

  /*
    안내가 떠 있는 동안 AI Agent 오브를 숨긴다(2026-11-02 — 화면 오른쪽 아래 줄 끝 [삭제] 를 짚었더니 오브와 그
    이름표가 그 버튼을 덮었다). 표시는 body 의 data 한 칸이고, 숨기는 일은 index.css 가 한다.
  */
  useEffect(() => {
    document.body.dataset.tourOn = '1';
    return () => {
      delete document.body.dataset.tourOn;
    };
  }, []);

  // 안내가 끝나면(닫기·건너뛰기·Esc·판번호 갱신 모두) 사이드바와 판·탭을 원래대로 돌려놓는다.
  useEffect(() => () => {
    setTourExpandedKeys([]);
    setTourOpenPanels([]);
    setTourOpenTab(null);
  }, []);

  const rect = useTargetRect(step);
  const subRects = useSubRects(step);
  const cardRef = useRef<HTMLDivElement>(null);
  const cardSize = useCardSize(cardRef);
  const cardPosition = keepOnScreen(placeCard(rect, step?.placement ?? 'auto', cardSize), cardSize);

  // 대상이 나타나길 기다리는 동안은 아무것도 띄우지 않는다 — 없으면 곧 건너뛴다(위 found).
  if (!step || !found) return null;
  const interactive = step.interactive === true;

  return createPortal(
    <div aria-live="polite">
      {/*
        클릭을 삼키는 막. 대상이 있을 때 어둠은 하이라이트의 그림자가 만들므로 여기는 투명하고,
        대상이 없는 걸음(환영·마무리)에서만 스스로 어두워진다.
        직접 해 보는 걸음에는 막이 없다 — 화면을 그대로 누르고 적는다.
      */}
      {/*
        직접 해 보는 걸음도 <b>어둡게 깐다</b>(사용자 지적 2026-11-02 — 흰 화면 위 흰 카드는 안 보였다).
        다만 막은 클릭을 삼키지 않는다(pointerEvents none) — 밝힌 자리를 그대로 누르고 적는다.
      */}
      {!interactive && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: rect ? 'transparent' : DIM }} />
      )}
      {interactive && !rect && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: DIM, pointerEvents: 'none' }} />
      )}

      {rect && interactive && (
        <div
          className="tour-sparkle"
          style={{
            position: 'fixed',
            top: rect.top - HOLE_PADDING - 3,
            left: rect.left - HOLE_PADDING - 3,
            width: rect.width + HOLE_PADDING * 2 + 6,
            height: rect.height + HOLE_PADDING * 2 + 6,
            borderRadius: '14px',
            pointerEvents: 'none',
            zIndex: 1001,
          }}
        />
      )}

      {rect && (
        <div
          style={{
            position: 'fixed',
            top: rect.top - HOLE_PADDING,
            left: rect.left - HOLE_PADDING,
            width: rect.width + HOLE_PADDING * 2,
            height: rect.height + HOLE_PADDING * 2,
            borderRadius: '12px',
            boxShadow: `0 0 0 9999px ${DIM}`,
            outline: '2px solid var(--accent-light)',
            outlineOffset: '1px',
            pointerEvents: 'none',
            zIndex: 1001,
            transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
          }}
        />
      )}

      {/*
        하이라이트 안의 하이라이트. 밝힌 영역이 넓을 때 "그중 어디" 를 눈으로 짚어 준다.
        반짝임은 CSS 로 둔다(`.tour-sparkle`) — keyframes 는 인라인 스타일로 쓸 수 없다.
      */}
      {subRects.map((sub, i) => (
        <div
          key={i}
          className="tour-sparkle"
          style={{
            position: 'fixed',
            top: sub.top - 4,
            left: sub.left - 4,
            width: sub.width + 8,
            height: sub.height + 8,
            pointerEvents: 'none',
            zIndex: 1001,
          }}
        />
      ))}

      <div
        ref={cardRef}
        role="dialog"
        aria-modal={!interactive}
        aria-labelledby="tour-step-title"
        style={{
          position: 'fixed',
          top: cardPosition.top,
          left: cardPosition.left,
          width: `min(${CARD_MAX_WIDTH}px, calc(100vw - ${EDGE * 2}px))`,
          // 절차가 긴 걸음은 카드가 화면보다 높아질 수 있다. 넘치면 카드 안에서 굴린다 —
          // 잘라 내면 마지막 절차가 소리 없이 사라지고, 그대로 두면 버튼이 화면 밖으로 나간다.
          maxHeight: `calc(100vh - ${EDGE * 2}px)`,
          overflowY: 'auto',
          zIndex: 1002,
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: '14px',
          boxShadow: '0 18px 40px rgba(15, 23, 42, 0.28)',
          padding: '16px',
        }}
      >
        <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
          <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent)' }}>
            안내 {index + 1} / {total}
            {interactive && (
              <span style={{ marginLeft: '6px', padding: '1px 7px', borderRadius: '999px', background: 'var(--accent-bg)' }}>
                직접 해 보기
              </span>
            )}
          </span>
          <button onClick={() => finish(false)} className="btn-ghost p-1" aria-label="안내 닫기">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <h2 id="tour-step-title" className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
          {step.title}
        </h2>
        <p style={{ marginTop: '6px', fontSize: '13px', lineHeight: 1.7, color: 'var(--text-secondary)', wordBreak: 'keep-all' }}>
          {emphasized(step.body)}
        </p>
        {/* 이 걸음에서 할 일 — 누르기 걸음은 [다음] 이 대신 누른다는 것을, 고르기 걸음은 고르면 넘어간다는 것을. */}
        {step.act && !blocked && (
          <p style={{ marginTop: '8px', fontSize: '12px', fontWeight: 700, color: '#0369a1', wordBreak: 'keep-all' }}>
            {step.act === 'click'
              ? '▶ 밝힌 버튼을 직접 누르거나, [다음]을 누르면 대신 눌러 드립니다.'
              : '▶ 밝힌 칸에서 값을 고르면 다음으로 넘어갑니다.'}
          </p>
        )}
        {blocked && (
          <p style={{ marginTop: '8px', fontSize: '12px', fontWeight: 700, color: '#b91c1c', wordBreak: 'keep-all' }}>{blocked}</p>
        )}

        <div className="flex items-center gap-2" style={{ marginTop: '14px' }}>
          <button
            onClick={() => finish(false)}
            className="btn-ghost"
            style={{ fontSize: '12px', padding: '4px 6px' }}
          >
            건너뛰기
          </button>
          <div className="ml-auto flex items-center gap-2">
            {index > 0 && (
              <button onClick={prev} className="btn-secondary" style={{ fontSize: '12px' }}>
                이전
              </button>
            )}
            <button onClick={blocked ? () => advanceFrom(index) : next} className="btn-primary" style={{ fontSize: '12px' }} autoFocus>
              {blocked ? '그냥 넘어가기' : isLast ? finishLabel : '다음'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * 대본 문장의 <b>&lt;b&gt;…&lt;/b&gt;</b> 만 굵게 그린다.
 *
 * <h2>왜 필요했나</h2>
 * 대본은 「이것만은 놓치면 안 된다」 를 굵게 적어 왔다(`pageGuides` 에 23곳). 그런데 카드가 문장을
 * <b>글자 그대로</b> 그려서, 화면에는 {@code <b>잔여 연차는 줄지 않습니다</b>} 처럼 태그가 보였다.
 * 안내를 읽는 사람에게 그것은 고장으로 읽히고, 강조하려던 문장이 오히려 가장 지저분해진다.
 *
 * <h2>왜 HTML 로 통째 그리지 않나</h2>
 * {@code dangerouslySetInnerHTML} 이면 한 줄이지만, 그 순간 <b>대본 문구가 실행되는 코드</b>가 된다.
 * 지금은 우리가 적는 상수뿐이라도, 나중에 이 자리에 서버 문구나 사용자 입력이 닿는 날 그대로 구멍이다.
 * 태그 하나를 위해 그 문을 열지 않는다 — 쪼개서 {@code <strong>} 으로 그리면 무엇이 들어와도 글자다.
 */
function emphasized(text: string): React.ReactNode {
  if (!text.includes('<b>')) {
    return text;
  }
  // 짝이 맞는 <b>…</b> 만 조각으로 잡는다. 짝이 없으면 아래에서 그냥 글자로 남는다.
  return text.split(/(<b>[\s\S]*?<\/b>)/).map((piece, index) => {
    const bold = piece.startsWith('<b>') && piece.endsWith('</b>');
    return bold
      ? <strong key={index} style={{ color: 'var(--text-primary)' }}>{piece.slice(3, -4)}</strong>
      : <span key={index}>{piece}</span>;
  });
}

/**
 * 지금 걸음이 가리키는 요소의 화면상 위치.
 *
 * 스크롤·창 크기 변경을 따라간다. 부드러운 스크롤은 이벤트가 띄엄띄엄 오므로 짧은 동안 프레임마다
 * 다시 재기도 한다 — 그러지 않으면 스크롤이 멎기 전까지 하이라이트가 뒤처져 엉뚱한 곳을 밝힌다.
 */
function useTargetRect(step: TourStep | null): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);
  const targetName = step?.target ?? null;

  useEffect(() => {
    if (!targetName) {
      setRect(null);
      return;
    }

    /*
      <b>아직 없을 수도 있다.</b> 이 걸음이 여는 판(옆판·모달)은 «열어 둬라» 를 알린 <b>다음 렌더</b>에
      생기므로, 여기서 한 번 찾고 없다고 포기하면 그 걸음은 영영 아무것도 못 밝힌다. 아래 프레임
      추적이 이미 돌고 있으니 <b>매 프레임 다시 찾게</b> 두면 나타나는 순간 붙는다.
    */
    let element = findTarget(targetName);
    let attempts = 0;
    const touched = new Set<HTMLElement>();
    let lastScrollAt = -Infinity;
    let userScrolled = false;
    const markUser = () => {
      userScrolled = true;
    };
    window.addEventListener('wheel', markUser, { passive: true });
    window.addEventListener('touchmove', markUser, { passive: true });

    // 값이 그대로면 상태를 갈지 않는다 — 아래 프레임 추적이 매 프레임 새 객체를 만들면
    // 아무것도 움직이지 않는데 초당 60번 다시 그린다.
    const update = () => {
      if (!element || !element.isConnected || !isVisible(element)) {
        element = findTarget(targetName);
      }
      if (!element) {
        setRect(null);
        return;
      }
      /*
        <b>열린 창 뒤에 가린 대상은 짚지 않는다</b>(2026-11-02). 창(.modal-overlay)이 떠 있는데 대상이 그 창 밖이면
        구멍이 창 위에 엉뚱한 조각을 뚫는다 — 카드만 띄워 「창을 저장하거나 닫으라」 는 문구가 말하게 한다.
      */
      if (document.querySelector('.modal-overlay') && !element.closest('.modal-overlay')) {
        setRect(null);
        return;
      }
      /*
        스크롤 판단은 <b>매 프레임</b> 다시 한다(사용자 지적 2026-11-02). 걸음이 막 바뀐 순간에는 대상이 아직
        그려지는 중이라(공사일보 편집 화면은 날씨·결재선을 1초 넘게 받은 뒤에야 자란다) 한 번만 보면 「화면 안」 으로
        잘못 보고 끝난다. 한 번 실제로 민 뒤, 또는 <b>사람이 스스로 굴린 뒤</b>에는 멈춘다 — 굴린 화면을 되당기지 않는다.
      */
      if (attempts < 5 && !userScrolled && performance.now() - lastScrollAt > 600) {
        /*
          <b>고정 상단바(56px) 밑은 화면 밖이다</b>(사용자 지적 2026-11-02 — 공사일보 [저장] 이 상단바 밑 y=0 에
          깔려 있었는데 브라우저는 「이미 보인다」 며 굴리지 않았고, 하이라이트는 상단바의 [직원찾기] 를 짚었다).
          굴릴 때 위·아래 여백을 두어 상단바 아래로 끌어내린다. 걸음이 끝나면 되돌린다(아래 정리).
        */
        element.style.scrollMarginTop = '84px';
        element.style.scrollMarginBottom = '24px';
        touched.add(element);
        const box = element.getBoundingClientRect();
        let pushed = true;
        /*
          <b>이미 보이는 것은 끌어오지 않는다</b>(사용자 지적 2026-08-26).

          <p>종전 규칙은 «아래가 화면 밖으로 나가면 가운데로 당긴다» 였다. 그래서 <b>화면보다 큰
          자리</b>(부서 오른쪽 판처럼 세로로 긴 칸)를 짚으면, 이미 눈앞에 있는데도 화면이 아래로
          밀리고 하이라이트가 그 사이 딴 데를 가리켰다. 큰 것을 가운데 두려면 위쪽을 잘라 내야 하니
          당연한 결과다.

          <p>지금은 <b>시작점이 보이느냐</b>만 본다 — 위로 잘렸거나(top &lt; 60) 아직 화면 아래에
          있을 때(top &gt; 화면 높이 − 80)만 옮기고, 그때도 {@code nearest} 로 <b>필요한 만큼만</b> 민다.
        */
        if (box.top < 64 || box.top > window.innerHeight - 80) {
          // jsdom 에는 scrollIntoView 가 없다. 없으면 하이라이트만 제자리에 그린다.
          element.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        } else if (box.left < 0 || box.right > window.innerWidth) {
          // 넓은 표의 가로 스크롤 밖(줄 끝 [저장] 같은 것) — 가로로도 데려온다.
          element.scrollIntoView?.({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        } else if (box.bottom > window.innerHeight - 12) {
          /*
            시작점은 보이는데 <b>아래가 잘린</b> 자리(사용자 지적 2026-11-02 — 공사일보 「인력 투입」 칸이
            화면 바닥에 반쯤 걸렸다). 화면에 들어가는 크기면 통째로 보이게, 더 크면 머리부터 보이게 민다.
          */
          const fits = box.height < window.innerHeight - 140;
          element.scrollIntoView?.({ block: fits ? 'center' : 'start', behavior: 'smooth' });
        } else {
          pushed = false;
        }
        /*
          밀었으면 0.6초 뒤에 <b>다시 본다</b> — 같은 때에 화면 쪽이 제 스크롤을 하면(편집 화면이 열리며 제 머리로
          굴린다) 이쪽 스크롤이 덮인다. 대상이 화면 안에 들어올 때까지, 다섯 번까지만.
        */
        if (pushed) {
          attempts += 1;
          lastScrollAt = performance.now();
        }
      }
      const measured = element;
      setRect((previous) => {
        const next = measure(measured);
        return sameRect(previous, next) ? previous : next;
      });
    };
    update();

    // scroll 은 capture 로 듣는다 — 스크롤하는 것이 window 가 아니라 안쪽 컨테이너일 수 있다.
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    /*
      걸음이 사는 동안 <b>매 프레임</b> 다시 잰다(사용자 지적 2026-11-02 — 「하이라이트 위치가 안 맞는다」).
      예전에는 45프레임(≈0.75초)만 따라갔다. 직접 해 보는 걸음에서는 사람이 누른 결과로 화면이 밀리고
      (새 줄·열린 칸), 자료가 늦게 와 표가 자라고, 대상이 몇 초 뒤에야 생긴다 — 그때마다 구멍이 옛 자리에
      남았다. 값이 같으면 상태를 안 갈므로(sameRect) 계속 돌아도 다시 그리지 않는다.
    */
    let raf = requestAnimationFrame(function tick() {
      update();
      raf = requestAnimationFrame(tick);
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('wheel', markUser);
      window.removeEventListener('touchmove', markUser);
      touched.forEach((node) => {
        node.style.scrollMarginTop = '';
        node.style.scrollMarginBottom = '';
      });
    };
  }, [targetName]);

  return rect;
}

/**
 * 보조 하이라이트가 가리키는 요소들의 위치.
 *
 * 바깥 하이라이트와 달리 <b>매번 요소를 다시 찾는다</b> — 투어가 대신 펼친 항목(사이드바 하위 목록)은
 * 이 효과가 도는 시점에 아직 DOM 에 없다. 나타나면 그때부터 반짝이고, 없으면 조용히 빠진다.
 *
 * 크기도 줄이지 않는다. 바깥 하이라이트는 구멍이 화면을 다 먹지 않도록 잘라 내지만, 이쪽은 이미
 * 그 안에 들어 있는 작은 영역이라 그대로 두는 편이 정확하다.
 */
function useSubRects(step: TourStep | null): Rect[] {
  const [rects, setRects] = useState<Rect[]>([]);
  /*
    <b>탭을 여는 걸음은 그 탭 버튼도 함께 반짝인다</b>(사용자 지시 2026-08-27).

    <p>탭을 대신 열어 주면 화면이 바뀌는데, 밝은 자리는 탭 <b>안</b>이라 «지금 어느 탭 이야기인가»
    가 화면 어디에도 없다. 카드 제목에 「원가관리 —」 를 적어 두어도 눈은 하이라이트를 보고 있다.
    대본마다 {@code subTargets} 에 같은 이름을 또 적게 하지 않고 여기서 얹는다 — 빠뜨릴 수 없다.
  */
  const names = [...(step?.subTargets ?? []), ...(step?.openTabKey ? [step.openTabKey] : [])].join('|');

  useEffect(() => {
    if (!names) {
      setRects([]);
      return;
    }

    const update = () =>
      setRects((previous) => {
        const next = names
          .split('|')
          .map(findTarget)
          .filter(isVisible)
          // 닫힌 메뉴(inert · 투명) 안의 것은 반짝이지 않는다 — 크기는 재지지만 눈에는 없어서, 테두리만
          // 허공에 떴다(사용자 지적 2026-11-02 「하이라이트 위치가 안 맞는다」 · 수행관리 목차).
          .filter((element) => !(element as HTMLElement).closest('[inert]'))
          .map((element) => {
            const box = (element as HTMLElement).getBoundingClientRect();
            return { top: box.top, left: box.left, width: box.width, height: box.height };
          });

        return sameRects(previous, next) ? previous : next;
      });
    update();

    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    let frames = 45;
    let raf = requestAnimationFrame(function tick() {
      update();
      if (--frames > 0) raf = requestAnimationFrame(tick);
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [names]);

  return rects;
}

/** 카드 실제 크기. 놓을 자리를 정하려면 높이를 알아야 하고, 높이는 문구 길이에 따라 다르다. */
function useCardSize(ref: React.RefObject<HTMLDivElement | null>): { width: number; height: number } {
  const [size, setSize] = useState({ width: CARD_MAX_WIDTH, height: 180 });

  // 렌더마다 그리기 전에 재고 고친다(useLayoutEffect) — 한 프레임 어긋난 자리에 카드가 번쩍이지 않도록.
  // 문구 길이가 걸음마다 달라 높이가 바뀌므로 의존성 목록을 두지 않는다({@code WidgetMasonry} 와 같은 방식).
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () => {
      const box = element.getBoundingClientRect();
      setSize((previous) =>
        // 같은 값으로 갱신하면 렌더가 끝없이 돈다. 값이 실제로 달라졌을 때만 바꾼다.
        Math.abs(previous.width - box.width) < 1 && Math.abs(previous.height - box.height) < 1
          ? previous
          : { width: box.width, height: box.height },
      );
    };
    measure();
  });

  return size;
}

function sameRect(a: Rect | null, b: Rect): boolean {
  return (
    a !== null &&
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

function sameRects(a: Rect[], b: Rect[]): boolean {
  return a.length === b.length && a.every((rect, i) => sameRect(rect, b[i] as Rect));
}

function findTarget(name: string): HTMLElement | null {
  /*
    <b>「영역>>글자」</b> — 표시가 달린 영역 안에서 그 글자의 버튼·탭·링크·칸을 짚는다(2026-11-02 · 수행관리 기능 설명).
    탭마다 기능 버튼이 수십 개라 하나하나 표시를 박는 대신 영역 하나에 표시를 두고 글자로 고른다. 글자가 바뀌면
    그 걸음은 조용히 빠지므로(대상 없음) 버튼 이름을 고칠 때는 대본도 함께 고친다. 글자가 같은 것이 먼저, 없으면
    그 글자로 시작하는 것(「전체 0」 같은 수가 붙은 칩).
  */
  const split = name.indexOf('>>');
  if (split > 0) {
    const scope = name.slice(0, split);
    const text = name.slice(split + 2);
    const candidates = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${scope}"]`)).flatMap((root) =>
      Array.from(root.querySelectorAll<HTMLElement>('button, a, [role="tab"], select, input, label')),
    );
    const label = (element: HTMLElement) =>
      (element.getAttribute('placeholder') || element.getAttribute('aria-label') || element.textContent || '').replace(/\s+/g, ' ').trim();
    const exact = candidates.filter((element) => label(element) === text);
    const pool = exact.length > 0 ? exact : candidates.filter((element) => label(element).startsWith(text));
    return pool.find((element) => isVisible(element)) ?? pool[0] ?? null;
  }
  // 같은 이름이 여럿이면(닫힌 메뉴 속 사본·줄마다 붙은 버튼) <b>보이는 첫 번째</b>를 짚는다.
  const all = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`));
  return all.find((element) => isVisible(element)) ?? all[0] ?? null;
}

/** 화면에 실제로 보이는가. `display:none`(offsetParent 없음)과 화면 밖으로 밀어 둔 것(모바일 서랍)을 함께 거른다. */
function isVisible(element: HTMLElement | null): boolean {
  if (!element || element.offsetParent === null) return false;

  const box = element.getBoundingClientRect();
  return box.width > 0 && box.height > 0 && box.right > 0 && box.left < window.innerWidth;
}

/**
 * 요소에서 <b>실제로 보이는 부분</b>. 넓은 표의 줄은 가로로 스크롤되는 상자 안에서 화면보다 훨씬 넓어,
 * 상자를 그대로 쓰면 하이라이트가 표 밖(왼쪽 메뉴)까지 번졌다(사용자 지적 2026-11-02). 넘침을 자르는
 * 조상들의 상자와 겹치는 부분만 남긴다.
 */
function visibleBox(element: HTMLElement): { top: number; left: number; right: number; bottom: number; width: number; height: number } {
  const box = element.getBoundingClientRect();
  let top = box.top;
  let left = box.left;
  let right = box.right;
  let bottom = box.bottom;
  for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    const clipsX = style.overflowX !== 'visible';
    const clipsY = style.overflowY !== 'visible';
    if (!clipsX && !clipsY) continue;
    const clip = node.getBoundingClientRect();
    if (clipsX) {
      left = Math.max(left, clip.left);
      right = Math.min(right, clip.right);
    }
    if (clipsY) {
      top = Math.max(top, clip.top);
      bottom = Math.min(bottom, clip.bottom);
    }
  }
  /*
    고정 상단바 아래만 화면이다(사용자 지적 2026-11-02 — 「화면이 내려가서 상단 헤더가 가린다」). 상단바 밑에 깔린
    부분은 보이지 않으므로 잘라 낸다 — 하이라이트가 상단바의 다른 버튼([직원찾기])을 짚지 않게. 상단바 자신을
    짚는 걸음(헤더 버튼 안내)은 대상이 상단바 안이라 자르지 않는다.
  */
  const header = document.querySelector<HTMLElement>('header');
  if (header && !header.contains(element) && getComputedStyle(header).position === 'fixed') {
    top = Math.max(top, header.getBoundingClientRect().bottom);
  }
  // 다 잘려 나갔으면(스크롤 밖) 원래 상자를 쓴다 — 스크롤이 곧 데려온다.
  if (right <= left || bottom <= top) return { top: box.top, left: box.left, right: box.right, bottom: box.bottom, width: box.width, height: box.height };
  return { top, left, right, bottom, width: right - left, height: bottom - top };
}

/** 키를 받은 곳이 입력 칸인가 — 그 키는 칸의 것이다. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * 대상의 위치. 화면보다 큰 대상(홈 위젯 전체 같은 것)은 보이는 만큼만, 그것도 화면 절반쯤으로 줄인다 —
 * 구멍이 화면을 다 먹으면 설명 카드를 놓을 자리가 없어진다.
 */
function measure(element: HTMLElement): Rect {
  const box = visibleBox(element);
  const top = Math.max(box.top, EDGE + HOLE_PADDING);
  const available = window.innerHeight - top - EDGE - HOLE_PADDING;
  const height = Math.max(24, Math.min(box.bottom - top, available, window.innerHeight * 0.55));

  return { top, left: box.left, width: box.width, height };
}

/** 하이라이트를 가리지 않는 자리를 찾는다. 원하는 방향부터 보고, 안 들어가면 아래·위·오른쪽·왼쪽 순으로 물러난다. */
function placeCard(
  rect: Rect | null,
  placement: TourPlacement,
  card: { width: number; height: number },
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!rect) {
    return { top: Math.max(EDGE, (vh - card.height) / 2), left: Math.max(EDGE, (vw - card.width) / 2) };
  }

  const hole = {
    top: rect.top - HOLE_PADDING,
    left: rect.left - HOLE_PADDING,
    right: rect.left + rect.width + HOLE_PADDING,
    bottom: rect.top + rect.height + HOLE_PADDING,
  };

  const order: TourPlacement[] =
    placement === 'auto' ? ['bottom', 'top', 'right', 'left'] : [placement, 'bottom', 'top', 'right', 'left'];

  for (const side of order) {
    if (side === 'bottom') {
      const top = hole.bottom + GAP;
      if (top + card.height <= vh - EDGE) return { top, left: clamp(hole.left, EDGE, vw - card.width - EDGE) };
    } else if (side === 'top') {
      const top = hole.top - GAP - card.height;
      if (top >= EDGE) return { top, left: clamp(hole.left, EDGE, vw - card.width - EDGE) };
    } else if (side === 'right') {
      const left = hole.right + GAP;
      if (left + card.width <= vw - EDGE) return { top: clamp(hole.top, EDGE, vh - card.height - EDGE), left };
    } else if (side === 'left') {
      const left = hole.left - GAP - card.width;
      if (left >= EDGE) return { top: clamp(hole.top, EDGE, vh - card.height - EDGE), left };
    }
  }

  // 어느 쪽에도 안 들어가는 좁은 화면. 하이라이트를 조금 가리더라도 화면 안에는 둔다.
  return { top: clamp(hole.bottom + GAP, EDGE, vh - card.height - EDGE), left: clamp(hole.left, EDGE, vw - card.width - EDGE) };
}

/**
 * 카드는 <b>언제나 화면 안</b>이다(사용자 지적 2026-11-02). 대상이 넓은 표의 가로 스크롤 밖에 있으면
 * 「왼쪽에 놓기」 가 화면 밖 좌표를 돌려주어 카드와 [다음] 이 함께 사라졌다.
 */
function keepOnScreen(at: { top: number; left: number }, card: { width: number; height: number }): { top: number; left: number } {
  return {
    top: clamp(at.top, EDGE, window.innerHeight - card.height - EDGE),
    left: clamp(at.left, EDGE, window.innerWidth - card.width - EDGE),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}
